import React, { useState, useEffect } from "react";
import { useCategoriesService } from "../hooks/useCategoriesService";
import {
  CategoryWithTemplates,
  CATEGORY_COLORS,
  CATEGORY_ICONS,
} from "../types/todo";

export const CategoriesManager: React.FC = () => {
  const {
    loading,
    error,
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    clearError,
  } = useCategoriesService();

  const [categories, setCategories] = useState<CategoryWithTemplates[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<CategoryWithTemplates | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: CATEGORY_COLORS[0],
    icon: CATEGORY_ICONS[0],
    is_public: false,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    const data = await getCategories();
    setCategories(data);
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await createCategory(formData);
    if (result) {
      await loadCategories();
      setShowCreateForm(false);
      resetForm();
    }
  };

  const handleUpdateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCategory) return;

    const result = await updateCategory(editingCategory.id, formData);
    if (result) {
      await loadCategories();
      setEditingCategory(null);
      resetForm();
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this category? This action cannot be undone."
      )
    ) {
      const success = await deleteCategory(id);
      if (success) {
        await loadCategories();
      }
    }
  };

  const startEdit = (category: CategoryWithTemplates) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      color: category.color || CATEGORY_COLORS[0],
      icon: category.icon || CATEGORY_ICONS[0],
      is_public: category.is_public,
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      color: CATEGORY_COLORS[0],
      icon: CATEGORY_ICONS[0],
      is_public: false,
    });
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setShowCreateForm(false);
    resetForm();
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="m-0 text-blue-500 text-2xl font-semibold">
          Zarządzanie kategoriami
        </h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-500 text-white border-none rounded-md cursor-pointer font-medium hover:bg-blue-600 transition-colors"
        >
          + Nowa kategoria
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-5 text-red-500 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={clearError}
            className="bg-none border-none text-red-500 cursor-pointer text-base hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Create/Edit Form */}
      {(showCreateForm || editingCategory) && (
        <div className="bg-white border border-gray-300 rounded-lg p-6 mb-6">
          <h3 className="m-0 mb-5 text-gray-800 text-xl font-semibold">
            {editingCategory ? "Edycja kategorii" : "Nowa kategoria"}
          </h3>

          <form
            onSubmit={
              editingCategory ? handleUpdateCategory : handleCreateCategory
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Nazwa *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md box-border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Ikona
                </label>
                <select
                  value={formData.icon}
                  onChange={(e) =>
                    setFormData({ ...formData, icon: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md box-border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {CATEGORY_ICONS.map((icon) => (
                    <option key={icon} value={icon}>
                      {icon}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-5">
              <label className="block mb-2 font-medium text-gray-700">
                Opis
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md box-border resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-end mb-5">
              <div>
                <label className="block mb-2 font-medium text-gray-700">
                  Kolor
                </label>
                <div className="flex gap-2 flex-wrap">
                  {CATEGORY_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded cursor-pointer transition-all hover:scale-110 ${
                        formData.color === color
                          ? "border-4 border-gray-800"
                          : "border border-gray-300"
                      }`}
                      style={{
                        backgroundColor: color,
                      }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={(e) =>
                      setFormData({ ...formData, is_public: e.target.checked })
                    }
                    className="w-4 h-4"
                  />
                  <span className="text-gray-700">Publiczna kategoria</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 bg-blue-500 text-white border-none rounded cursor-pointer font-medium transition-colors ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-600"
                }`}
              >
                {loading
                  ? "Zapisywanie..."
                  : editingCategory
                  ? "Zapisz"
                  : "Utwórz"}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 bg-transparent text-gray-600 border border-gray-300 rounded cursor-pointer hover:bg-gray-50 transition-colors"
              >
                Anuluj
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories Grid */}
      {loading && categories.length === 0 ? (
        <div className="text-center py-10 text-gray-600">
          Ładowanie katerogii...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {categories.map((category) => (
            <div
              key={category.id}
              className="bg-white border border-gray-300 rounded-lg p-5 relative hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                  style={{
                    backgroundColor: category.color || CATEGORY_COLORS[0],
                  }}
                >
                  {category.icon || "📝"}
                </div>
                <div className="flex-1">
                  <h3 className="m-0 mb-1 text-lg font-semibold text-gray-800">
                    {category.name}
                  </h3>
                  <div className="text-sm text-gray-600">
                    {category.templates_count || 0} harmonogramów
                    {category.is_public && (
                      <span className="ml-2 px-2 py-1 bg-gray-100 rounded text-xs">
                        Publiczna
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {category.description && (
                <p className="m-0 mb-4 text-gray-600 text-sm leading-relaxed">
                  {category.description}
                </p>
              )}

              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => startEdit(category)}
                  className="px-3 py-1.5 bg-transparent text-blue-500 border border-blue-500 rounded cursor-pointer text-sm hover:bg-blue-50 transition-colors"
                >
                  Edytuj
                </button>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  className="px-3 py-1.5 bg-transparent text-red-500 border border-red-500 rounded cursor-pointer text-sm hover:bg-red-50 transition-colors"
                >
                  Usuń
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {categories.length === 0 && !loading && (
        <div className="text-center py-15 px-5 text-gray-600">
          <div className="text-5xl mb-4">📂</div>
          <h3 className="m-0 mb-2 text-xl font-semibold">No categories yet</h3>
          <p className="m-0 mb-5">
            Create your first category to organize your todo list templates.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-6 py-3 bg-blue-500 text-white border-none rounded-md cursor-pointer font-medium hover:bg-blue-600 transition-colors"
          >
            Create First Category
          </button>
        </div>
      )}
    </div>
  );
};
